import React, { useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";

import Alerts from "/src/util/alert";

import { USER_LOGIN } from "../../config/lang";
import { auth } from "../../util/action";
import { login } from "../../util/redux/actions";
import { translate } from "../../util/util";
import AuthShell, { useAuthLang, useClearErrorsOnLangChange } from "./AuthShell";

/* Roles allowed into the supervisor dashboard. Anything else authenticates but is turned away. */
const ALLOWED_ROLES = ["supervisor", "supervisor_manager"];

function Login({ dispatch, history }) {
  const { lang, setLang, t } = useAuthLang();
  const [form] = Form.useForm();
  useClearErrorsOnLangChange(form, lang);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);

  const submit = ({ phone_number, password }) => {
    setFailure(null);
    setLoading(true);

    auth({ phone_number: phone_number.trim(), password })
      .then(res => {
        if (!res || !res.user) {
          setFailure(translate(USER_LOGIN, "login_error_msg", lang));
          return;
        }
        if (!ALLOWED_ROLES.includes(res.user.role)) {
          setFailure(t("err_role"));
          return;
        }

        const me = {
          ...res.user,
          key: res.key,
          company: res.related?.company,
          client: res.related?.client,
          county: res.related?.county,
        };

        Alerts.success(translate(USER_LOGIN, "login_success_msg", lang));
        dispatch(login(me));
        history.replace("/photo-review");
        /* The legacy layout reads persisted state at module scope, so a reload is still the
           reliable way to hand off after sign-in. Remove once Layout derives from the store. */
        window.location.reload();
      })
      .catch(err => setFailure(String(err && err.message ? err.message : err)))
      .finally(() => setLoading(false));
  };

  return (
    <AuthShell lang={lang} setLang={setLang} t={t}>
      <Form
        form={form}
        className="auth-form"
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        /* onChange, not onSubmit: a field that was never touched is never validated, so a
           pristine form stays unflagged — but once an error is showing it clears as soon as
           the user fixes it, instead of sitting there until the next submit. */
        validateTrigger="onChange">
        <div className="auth-form__head">
          <h2 className="auth-title">{t("signin_title")}</h2>
          <p className="auth-subtitle">{t("signin_sub")}</p>
        </div>

        <div className="auth-fields">
          <Form.Item name="phone_number" label={t("phone_label")} rules={[{ required: true, whitespace: true, message: t("err_required") }]}>
            <Input size="large" type="tel" autoComplete="tel" autoFocus placeholder={t("phone_ph")} onChange={() => setFailure(null)} />
          </Form.Item>

          <Form.Item name="password" label={t("password_label")} rules={[{ required: true, message: t("err_required") }]}>
            <Input.Password
              size="large"
              autoComplete="current-password"
              placeholder={t("password_ph")}
              onChange={() => setFailure(null)}
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              visibilityToggle={{ "aria-label": t("show_password") }}
            />
          </Form.Item>

          <div className="auth-linkrow">
            <Link className="auth-link" to="/forgot-password">
              {t("forgot_link")}
            </Link>
          </div>
        </div>

        <div className="auth-actions">
          <Button className="auth-submit" type="primary" size="large" htmlType="submit" loading={loading}>
            {t("signin_btn")}
          </Button>

          {failure ? <Alert className="auth-alert" type="error" showIcon title={failure} role="alert" /> : null}

          <p className="auth-note">{t("role_note")}</p>
        </div>
      </Form>
    </AuthShell>
  );
}

export default withRouter(connect(state => ({ me: state.me }))(Login));
